import { useMemo, useState } from 'react';
import moment from 'moment';
import { ChevronLeft, ChevronRight, CalendarDays, List, Heart, Camera, CalendarClock, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import { MonthGrid } from './MonthGrid';
import { DayAgenda, UpcomingList } from './DayAgenda';
import { KIND, kindOf, groupByDay, dayKey } from './calendarUtils';

const EXTRA_LABELS = { main: 'Düğün', shooting: 'Fotoğraf', video: 'Video', outdoor: 'Dış Çekim', ceremony: 'Nikah', henna: 'Kına', engagement: 'Nişan', other: 'Diğer' };

const Stat = ({ icon: Icon, label, value, cls, testId }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5" data-testid={testId}>
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cls}`}><Icon className="h-4 w-4" /></div>
    <div>
      <p className="text-lg font-bold leading-none">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-1">{label}</p>
    </div>
  </div>
);

// Yeni takvim görünümü: ay ızgarası + seçili gün paneli + yaklaşan liste (telefonda sade)
export const WeddingCalendar = ({ date, onNavigate, events, holidayOf, onOpenEvent, onAddToDay }) => {
  const [view, setView] = useState(() => (window.innerWidth < 640 ? 'list' : 'month'));
  const [selectedDay, setSelectedDay] = useState(new Date());
  const real = useMemo(() => events.filter(e => !e.resource?.isHoliday), [events]);
  const byDay = useMemo(() => groupByDay(real), [real]);

  const monthEvents = real.filter(e => moment(e.start).isSame(date, 'month'));
  const weddingDays = new Set(monthEvents.filter(e => kindOf(e) === 'wedding').map(e => dayKey(e.start)));
  const freeWeekends = (() => {
    let n = 0;
    const end = moment(date).endOf('month');
    for (let d = moment(date).startOf('month'); d.isSameOrBefore(end); d.add(1, 'day')) {
      if (d.isoWeekday() >= 6 && !byDay[d.format('YYYY-MM-DD')] && d.isSameOrAfter(moment(), 'day')) n += 1;
    }
    return n;
  })();
  const upcoming = real.filter(e => moment(e.start).isSameOrAfter(moment(), 'day')).sort((a, b) => a.start - b.start).slice(0, 60);

  const go = (d) => onNavigate(moment(date).add(d, 'month').toDate());
  const selectDay = (d) => {
    setSelectedDay(d);
    if (!moment(d).isSame(date, 'month')) onNavigate(d);
  };

  return (
    <div className="space-y-4" data-testid="wedding-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => go(-1)} data-testid="cal-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <h2 className="text-xl sm:text-2xl font-bold capitalize min-w-[150px] text-center" data-testid="cal-month-title">{moment(date).format('MMMM YYYY')}</h2>
          <Button variant="outline" size="icon" onClick={() => go(1)} data-testid="cal-next-btn"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => { onNavigate(new Date()); setSelectedDay(new Date()); }} data-testid="cal-today-btn">Bugün</Button>
        </div>
        <div className="inline-flex rounded-full border border-border p-0.5 bg-muted/40">
          {[['month', 'Ay', CalendarDays], ['list', 'Liste', List]].map(([k, l, I]) => (
            <button key={k} type="button" onClick={() => setView(k)} data-testid={`cal-view-${k}`}
              className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${view === k ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'}`}>
              <I className="h-3.5 w-3.5" /> {l}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <Stat icon={Heart} label="Bu ay düğün" value={weddingDays.size} cls="bg-rose-500/15 text-rose-600" testId="cal-stat-weddings" />
        <Stat icon={Camera} label="Bu ay toplam etkinlik" value={monthEvents.length} cls="bg-sky-500/15 text-sky-600" testId="cal-stat-events" />
        <Stat icon={CalendarClock} label="Yaklaşan (tümü)" value={upcoming.length} cls="bg-amber-500/15 text-amber-600" testId="cal-stat-upcoming" />
        <Stat icon={Sun} label="Boş hafta sonu günü" value={freeWeekends} cls="bg-emerald-500/15 text-emerald-600" testId="cal-stat-free" />
      </div>

      {view === 'month' ? (
        <div className="grid lg:grid-cols-[1fr_340px] gap-4 items-start">
          <MonthGrid date={date} byDay={byDay} holidayOf={holidayOf} selectedDay={selectedDay} onSelect={selectDay} />
          <div className="lg:sticky lg:top-20">
            <DayAgenda day={selectedDay} events={byDay[dayKey(selectedDay)] || []} holiday={holidayOf(selectedDay)}
              extraLabels={EXTRA_LABELS} onOpen={onOpenEvent} onAdd={onAddToDay} />
          </div>
        </div>
      ) : (
        <UpcomingList events={upcoming} extraLabels={EXTRA_LABELS} onOpen={onOpenEvent} />
      )}

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground" data-testid="cal-legend">
        {Object.values(KIND).map(k => <span key={k.label} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-full ${k.dot}`} />{k.label}</span>)}
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />Resmi Tatil</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />Dini Bayram</span>
      </div>
    </div>
  );
};
